// ABOUTME: Shared, un-forked crossing list — renders on the parent, tablet, and child surfaces.
// ABOUTME: Splits open vs done-today by status on the items it's handed (Issue #1939).

import { Box, List, Typography } from '@mui/material';
import CrossingListRow from './CrossingListRow';
import type { CrossingListItemWithChild } from '../../hooks/useCrossings';
import { CrossingStatus } from '../../types/api';

interface CrossingListProps {
  items: CrossingListItemWithChild[];
  onOpen: (crossingId: number) => void;
  groupByChild?: boolean;
  childNameById?: Record<number, string>;
  emptyState?: React.ReactNode;
}

function isOpen(status: CrossingStatus): boolean {
  return status === CrossingStatus.NotStarted || status === CrossingStatus.InProgress;
}

function Rows({ items, onOpen }: { items: CrossingListItemWithChild[]; onOpen: (id: number) => void }) {
  return (
    <List component="ul" disablePadding>
      {items.map(item => (
        <CrossingListRow key={item.id} item={item} onOpen={onOpen} />
      ))}
    </List>
  );
}

export default function CrossingList({ items, onOpen, groupByChild, childNameById, emptyState }: CrossingListProps) {
  if (items.length === 0) {
    return <Box>{emptyState ?? <Typography color="text.secondary">Nothing is waiting</Typography>}</Box>;
  }

  const open = items.filter(i => isOpen(i.status));
  const done = items.filter(i => i.status === CrossingStatus.Complete);

  if (!groupByChild) {
    return (
      <Box>
        <Rows items={open} onOpen={onOpen} />
        {done.length > 0 && (
          <Box sx={{ mt: 2, opacity: 0.7 }}>
            <Typography variant="subtitle2">Done today</Typography>
            <Rows items={done} onOpen={onOpen} />
          </Box>
        )}
      </Box>
    );
  }

  const childIds = Array.from(new Set(items.map(i => i.childId)));
  return (
    <Box>
      {childIds.map(childId => {
        const childOpen = open.filter(i => i.childId === childId);
        const childDone = done.filter(i => i.childId === childId);
        if (childOpen.length === 0 && childDone.length === 0) return null;
        return (
          <Box key={childId} sx={{ mb: 2 }}>
            <Typography variant="subtitle1">{childNameById?.[childId] ?? `Child ${childId}`}</Typography>
            <Rows items={childOpen} onOpen={onOpen} />
            {childDone.length > 0 && (
              <Box sx={{ mt: 1, opacity: 0.7 }}>
                <Typography variant="subtitle2">Done today</Typography>
                <Rows items={childDone} onOpen={onOpen} />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
